/**
 * Keyboard and gamepad input for the station screens (plan §6 M2-18), separate from the DOM wiring so it is
 * tested headless. Inputs go to the React tree through the `NavBus`, synchronously (so Launch can request pointer
 * lock inside the key gesture).
 *
 * - Active only while a UI screen is up and the pause menu is closed. The pause state comes from the bus (`pause` /
 *   `resume`), not `FrameInfo.paused`: before the first gesture the loop is paused too, and the title screen must
 *   still take input then.
 * - A key the pause menu already handled (`defaultPrevented`) never reaches the station screen, and the pad nav is
 *   disarmed on pause and resume, so the press that resumed (A on Resume) does not also activate a station item.
 * - Handled keys are stopped (`preventDefault`, `stopPropagation`) so they never reach the flight bindings; Esc and P
 *   are not UI keys and stay with the loop (the pause menu opens over the station).
 */
import { disarmPadNav } from '../../hud/menu'
import type { PadLike } from '../../input/playerInput'
import type { UiStoreApi } from '../../ui/contracts'
import { keyToNavInput, stepUiPad, type NavBus, type UiPadState } from '../../ui/nav'

/** The parts of a `KeyboardEvent` the handler reads. */
export interface KeyLike {
  readonly code: string
  readonly repeat: boolean
  readonly ctrlKey: boolean
  readonly metaKey: boolean
  readonly altKey: boolean
  readonly defaultPrevented: boolean
  readonly target: EventTarget | null
  preventDefault(): void
  stopPropagation(): void
}

export interface StationInput {
  readonly store: Pick<UiStoreApi, 'getState'>
  readonly bus: NavBus
  readonly pad: UiPadState
  /** The pause menu is open. */
  paused: boolean
}

export function createStationInput(store: Pick<UiStoreApi, 'getState'>, bus: NavBus, pad: UiPadState): StationInput {
  return { store, bus, pad, paused: false }
}

/** A UI screen is up and the pause menu is closed. */
export function stationActive(s: StationInput): boolean {
  return !s.paused && s.store.getState().screen !== 'none'
}

/** The pause menu opened or closed (bus `pause` / `resume`). */
export function setStationPaused(s: StationInput, paused: boolean): void {
  s.paused = paused
  disarmPadNav(s.pad.nav)
}

function isTextInput(target: EventTarget | null): boolean {
  return typeof HTMLInputElement !== 'undefined' && target instanceof HTMLInputElement && target.type !== 'file'
}

/** The capture-phase keydown handler. */
export function stationKeyDown(s: StationInput, e: KeyLike): void {
  if (e.defaultPrevented || !stationActive(s)) return
  if (isTextInput(e.target) || e.ctrlKey || e.metaKey || e.altKey) return
  const input = keyToNavInput(e.code, e.repeat)
  // Auto-repeats of UI action keys are swallowed too, so nothing leaks into the flight bindings.
  if (input === null && (!e.repeat || keyToNavInput(e.code, false) === null)) return
  e.preventDefault()
  e.stopPropagation()
  if (input !== null) s.bus.dispatch(input)
}

/**
 * Per-frame pad polling (allocation-free, D-014). Runs every frame, active or not, so the press that opened a screen
 * does not count as a fresh edge.
 */
export function stationFrame(s: StationInput, pad: PadLike | null, dt: number): void {
  const input = stepUiPad(s.pad, pad, dt)
  if (input !== null && stationActive(s)) s.bus.dispatch(input)
}
